import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Container,
  Avatar,
  Paper,
  Button,
} from '@mui/material';
import { Send as SendIcon, School as SchoolIcon, AdminPanelSettings as AdminIcon } from '@mui/icons-material';
import { useAuth } from './auth/AuthContext';
import api from '../utils/api';
import io from 'socket.io-client';
import AOS from 'aos';
import 'aos/dist/aos.css';

const SOCKET_URL = 'https://kidssock.onrender.com';
const socket = io(SOCKET_URL, { transports: ['websocket'] });

const AdminSupportChat = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { conversationId, schoolId, schoolName, schoolAddress } = location.state || {};
  const { isAdminAuthenticated } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    AOS.init({ duration: 800, easing: 'ease-in-out', once: true });
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    const fetchMessages = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!isAdminAuthenticated) {
          throw new Error('Please login as admin first');
        }

        const response = await api.get('/api/admin/support/admin/messages', {
          params: { conversationId },
        });

        if (response.data && Array.isArray(response.data.messages)) {
          setMessages(response.data.messages);
        } else {
          throw new Error('Invalid messages data format');
        }

        await api.post('/api/admin/support/admin/mark-read', { conversationId });
        scrollToBottom();
      } catch (err) {
        console.error('Error fetching messages:', err);
        setError(err.response?.data?.message || err.message || 'Failed to load messages');
      } finally {
        setLoading(false);
      }
    };

    if (conversationId) {
      fetchMessages();

      socket.emit('joinSupportChat', conversationId);

      socket.on('newSupportMessage', (message) => {
        if (message.conversationId === conversationId) {
          setMessages((prev) => [...prev, message]);
          scrollToBottom();
        }
      });
    } else {
      setError('No conversation ID provided');
      setLoading(false);
    }

    return () => {
      socket.off('newSupportMessage');
    };
  }, [conversationId, isAdminAuthenticated]);

  const sendMessage = async () => {
    if (!newMessage.trim()) return;

    try {
      setError(null);
      const response = await api.post('/api/admin/support/admin/messages', {
        conversationId,
        schoolId,
        text: newMessage,
      });

      if (response.data && response.data.success) {
        setMessages((prev) => [
          ...prev,
          {
            senderId: 'vendor-admin',
            receiverId: schoolId,
            text: newMessage,
            createdAt: new Date().toISOString(),
            senderType: 'admin',
            _id: Date.now().toString(),
          },
        ]);
        setNewMessage('');
        scrollToBottom();
      } else {
        throw new Error('Failed to send message');
      }
    } catch (err) {
      console.error('Error sending message:', err);
      setError(err.response?.data?.message || err.message || 'Failed to send message');
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          bgcolor: '#f5f5f5',
        }}
      >
        <CircularProgress size={60} sx={{ color: '#6200ee' }} />
        <Typography variant="h6" sx={{ mt: 3, color: '#2c3e50' }}>
          Loading chat...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          bgcolor: '#f5f5f5',
        }}
      >
        <Typography variant="h6" color="error" sx={{ mb: 3, textAlign: 'center' }}>
          {error}
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate(-1)}
          sx={{ bgcolor: '#6200ee', '&:hover': { bgcolor: '#7f39fb' }, px: 4, py: 1.5 }}
        >
          Go Back
        </Button>
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4, bgcolor: '#f5f5f5', minHeight: '100vh' }} data-aos="fade-down">
      <Paper sx={{ bgcolor: '#6200ee', p: 2, mb: 2, borderRadius: 2 }} data-aos="fade-up">
        <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold' }}>
          {schoolName}
        </Typography>
        <Typography variant="body2" sx={{ color: '#fff', mt: 0.5 }}>
          {schoolAddress}
        </Typography>
      </Paper>
      <Paper
        sx={{
          height: 'calc(100vh - 250px)',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 2,
          overflow: 'hidden',
        }}
        data-aos="fade-up"
      >
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 2,
            bgcolor: '#f5f5f5',
          }}
        >
          {messages.map((message, index) => (
            <Box
              key={message._id || index}
              sx={{
                display: 'flex',
                justifyContent: message.senderType === 'admin' ? 'flex-end' : 'flex-start',
                mb: 2,
                maxWidth: '100%',
              }}
            >
              {message.senderType !== 'admin' && (
                <Avatar sx={{ bgcolor: '#6200ee', mr: 1, mt: 1 }}>
                  <SchoolIcon sx={{ color: '#fff' }} />
                </Avatar>
              )}
              <Box
                sx={{
                  maxWidth: '70%',
                  bgcolor: message.senderType === 'admin' ? '#6200ee' : '#e5e5ea',
                  color: message.senderType === 'admin' ? '#fff' : '#000',
                  p: 2,
                  borderRadius: 3,
                  borderBottomRightRadius: message.senderType === 'admin' ? 0 : 3,
                  borderBottomLeftRadius: message.senderType === 'admin' ? 3 : 0,
                }}
              >
                <Typography variant="body1">{message.text}</Typography>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    textAlign: 'right',
                    mt: 0.5,
                    opacity: 0.7,
                    color: message.senderType === 'admin' ? '#fff' : '#000',
                  }}
                >
                  {new Date(message.createdAt).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </Typography>
              </Box>
              {message.senderType === 'admin' && (
                <Avatar sx={{ bgcolor: '#6200ee', ml: 1, mt: 1 }}>
                  <AdminIcon sx={{ color: '#fff' }} />
                </Avatar>
              )}
            </Box>
          ))}
          <div ref={messagesEndRef} />
        </Box>
        <Box
          sx={{
            p: 2,
            bgcolor: '#fff',
            borderTop: '1px solid #eee',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <TextField
            fullWidth
            multiline
            maxRows={4}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type a message..."
            sx={{ mr: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}
            variant="outlined"
          />
          <IconButton
            onClick={sendMessage}
            sx={{ bgcolor: '#6200ee', color: '#fff', '&:hover': { bgcolor: '#7f39fb' } }}
          >
            <SendIcon />
          </IconButton>
        </Box>
      </Paper>
    </Container>
  );
};

export default AdminSupportChat;